import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { automationsActions, alertActions } from '@actions';
import { automationsService } from '@services';
import { removeFirstOccurrence } from '@helpers/arrays';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { handleApiError } from '@helpers/api';
import usePermissions from '@hooks/usePermissions';
import AutomationsTable from './AutomationsTable';
import Editor from './Editor';

const AutomationsView = ({ editor, setEditor }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hasFullAccess } = usePermissions();

    const automations = useSelector(state => state.automations.data);
    const totalCount = useSelector(state => state.automations.totalCount);
    const labels = useSelector(state => state.labels?.data || []);

    const [toDelete, setToDelete] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [togglePendingIds, setTogglePendingIds] = useState([]);

    const canWrite = hasFullAccess('campaigns');

    useEffect(() => {
        dispatch(automationsActions.get(page, rowsPerPage));
    }, [dispatch, page, rowsPerPage]);

    const refresh = () => dispatch(automationsActions.get(page, rowsPerPage));

    // Held through the refetch too — re-enabling earlier shows the stale switch position.
    const onStatusChange = async (automation, action) => {
        setTogglePendingIds(prev => [...prev, automation.id]);
        try {
            await automationsService[action](automation.id);
            await refresh();
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        } finally {
            setTogglePendingIds(prev => removeFirstOccurrence(prev, automation.id));
        }
    };

    const onDelete = async () => {
        try {
            await automationsService.remove(toDelete.id);
            setToDelete(null);
            refresh();
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
    };

    return (
        <>
            <AutomationsTable
                automations={automations}
                canWrite={canWrite}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onChangePage={setPage}
                onChangeRowsPerPage={value => {
                    setRowsPerPage(value);
                    setPage(0);
                }}
                onEdit={automation => setEditor({ open: true, id: automation.id })}
                onView={automation => setEditor({ open: true, id: automation.id })}
                onDelete={setToDelete}
                onActivate={automation => onStatusChange(automation, 'activate')}
                onPause={automation => onStatusChange(automation, 'pause')}
                togglePendingIds={togglePendingIds}
            />

            {editor.open && (
                <Editor
                    automationId={editor.id}
                    labels={labels}
                    readOnly={!canWrite}
                    onClose={() => setEditor({ open: false, id: null })}
                    onSaved={() => {
                        setEditor({ open: false, id: null });
                        refresh();
                    }}
                />
            )}

            {toDelete && (
                <ModalConfirm
                    isOpen={!!toDelete}
                    handleClose={() => setToDelete(null)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default AutomationsView;
